import { useEffect, useState } from 'react';
import { api } from '../../services/api';

interface SyncState {
  id: number;
  token_expires_at: string | null;
  last_sync_at: string | null;
  last_record_time: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  last_records_pulled: number;
  last_records_inserted: number;
  last_records_skipped: number;
  stamp_offset_hours: number;
  updated_at: string;
}

interface StatusResponse {
  configured: boolean;
  syncState: SyncState | null;
}

interface UnmappedUser {
  crosschex_user_id: string;
  workno: string;
  name: string | null;
  first_seen_at: string;
  last_seen_at: string;
  punch_count: number;
}

interface MappedEmployee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string | null;
  crosschex_workno: string;
  is_active: boolean;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status?: string;
  is_active?: boolean;
}

function fmtDate(v: string | null): string {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

export function CrosschexPage() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [unmapped, setUnmapped] = useState<UnmappedUser[]>([]);
  const [mapped, setMapped] = useState<MappedEmployee[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [mapSelections, setMapSelections] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, unmappedRes, mappedRes, empRes] = await Promise.all([
        api.get<StatusResponse>('/crosschex/status'),
        api.get<UnmappedUser[]>('/crosschex/unmapped'),
        api.get<MappedEmployee[]>('/crosschex/mapped'),
        api.get<Employee[]>('/employees'),
      ]);
      setStatus(statusRes);
      setUnmapped(unmappedRes);
      setMapped(mappedRes);
      setEmployees((empRes as any[]).filter((e) => e.status ? e.status === 'active' : e.is_active !== false));
    } catch (err: any) {
      setMessage({ text: `Failed to load: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const res = await api.post<{ ok: boolean; message: string }>('/crosschex/test-connection', {});
      setMessage({ text: res.data.message, type: res.data.ok ? 'success' : 'error' });
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setTesting(false);
      load();
    }
  };

  const handleSync = async (windowMinutes?: number) => {
    setSyncing(true);
    setMessage({ text: 'Syncing...', type: 'info' });
    try {
      const res = await api.post<any>('/crosschex/sync', windowMinutes ? { windowMinutes } : {});
      const r = res.data;
      setMessage({
        text: `Synced: ${r.inserted} inserted, ${r.skipped} duplicates, ${r.unmapped} unmapped (pulled ${r.pulled})`,
        type: 'success',
      });
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setSyncing(false);
      load();
    }
  };

  const handleMap = async (workno: string) => {
    const employeeId = mapSelections[workno];
    if (!employeeId) return;
    try {
      const res = await api.post<{ success: boolean; drained?: number }>('/crosschex/map', { workno, employeeId });
      const drained = res.data?.drained ?? 0;
      setMessage({
        text: drained > 0
          ? `Mapped workno ${workno} — ${drained} queued punch${drained === 1 ? '' : 'es'} added to time records`
          : `Mapped workno ${workno}`,
        type: 'success',
      });
      load();
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  const handleSetOffset = async (hours: number) => {
    try {
      await api.post('/crosschex/stamp-offset', { hours });
      setMessage({
        text: hours === 0
          ? 'DST adjustment disabled. CrossChex timestamps will be used as-is.'
          : `DST adjustment set to ${hours > 0 ? '+' : ''}${hours}h. All incoming CrossChex stamps will be shifted by this amount.`,
        type: 'success',
      });
      load();
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  const handleUnmap = async (employeeId: string) => {
    if (!confirm('Remove CrossChex mapping for this employee?')) return;
    try {
      await api.del(`/crosschex/map/${employeeId}`);
      setMessage({ text: 'Unmapped', type: 'success' });
      load();
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  if (loading && !status) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  const configured = status?.configured ?? false;
  const state = status?.syncState;
  const syncStatusColor =
    state?.last_sync_status === 'ok'
      ? 'text-green-700 bg-green-50 border-green-200'
      : state?.last_sync_status === 'error'
        ? 'text-red-700 bg-red-50 border-red-200'
        : 'text-gray-700 bg-gray-50 border-gray-200';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CrossChex Time Clock Sync</h1>
          <p className="text-sm text-gray-600 mt-1">
            Syncs punches from CrossChex Cloud into employee time records. Runs automatically every 2 minutes.
          </p>
        </div>
      </div>

      {message && (
        <div
          className={`p-3 rounded border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : message.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {!configured && (
        <div className="p-4 rounded border bg-amber-50 border-amber-200 text-amber-900 text-sm">
          <strong>Not configured.</strong> Add <code>CROSSCHEX_APP_KEY</code> and <code>CROSSCHEX_APP_SECRET</code> to
          the server <code>.env</code> file and rebuild the API container.
        </div>
      )}

      {/* Status card */}
      <div className="bg-white rounded border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Sync Status</h2>
          <div className="flex gap-2">
            <button
              onClick={handleTestConnection}
              disabled={!configured || testing}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded disabled:opacity-50"
            >
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              onClick={() => handleSync()}
              disabled={!configured || syncing}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
            >
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
            <button
              onClick={() => handleSync(60 * 24 * 7)}
              disabled={!configured || syncing}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded disabled:opacity-50"
              title="Pull the last 7 days"
            >
              Backfill 7 days
            </button>
          </div>
        </div>

        {state && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Last Sync</div>
              <div className="font-medium">{fmtDate(state.last_sync_at)}</div>
            </div>
            <div>
              <div className="text-gray-500">Last Record Time</div>
              <div className="font-medium">{fmtDate(state.last_record_time)}</div>
            </div>
            <div>
              <div className="text-gray-500">Token Expires</div>
              <div className="font-medium">{fmtDate(state.token_expires_at)}</div>
            </div>
            <div>
              <div className="text-gray-500">Status</div>
              <div className={`inline-block px-2 py-0.5 rounded border ${syncStatusColor}`}>
                {state.last_sync_status || 'never run'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Last Pulled</div>
              <div className="font-medium">{state.last_records_pulled}</div>
            </div>
            <div>
              <div className="text-gray-500">Last Inserted</div>
              <div className="font-medium">{state.last_records_inserted}</div>
            </div>
            <div>
              <div className="text-gray-500">Last Skipped (dup)</div>
              <div className="font-medium">{state.last_records_skipped}</div>
            </div>
            <div>
              <div className="text-gray-500">Unmapped Users</div>
              <div className="font-medium">{unmapped.length}</div>
            </div>
          </div>
        )}

        {state?.last_sync_error && (
          <div className="text-sm p-2 rounded bg-red-50 border border-red-200 text-red-800 font-mono">
            {state.last_sync_error}
          </div>
        )}
      </div>

      {/* DST adjustment */}
      <div className="bg-white rounded border p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Daylight Saving Time Adjustment</h2>
        <p className="text-sm text-gray-600 mb-3">
          The CrossChex device firmware does not properly apply DST to the timestamps it sends us. When operators add a
          +1h bias on the device to make the clock face read correctly during DST, that same +1h gets added to the
          UTC stamps — putting our records 1 hour ahead of reality. Use this toggle to cancel that out.
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700">Current offset:</span>
          <span className="font-mono font-semibold text-base">
            {Number(state?.stamp_offset_hours || 0) === 0
              ? '0 hours (no adjustment)'
              : `${Number(state?.stamp_offset_hours) > 0 ? '+' : ''}${Number(state?.stamp_offset_hours)} hours`}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => handleSetOffset(-1)}
              disabled={Number(state?.stamp_offset_hours || 0) === -1}
              className="px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-40"
            >
              Enable DST (−1h)
            </button>
            <button
              onClick={() => handleSetOffset(0)}
              disabled={Number(state?.stamp_offset_hours || 0) === 0}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded disabled:opacity-40"
            >
              Disable (0h)
            </button>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500 space-y-1">
          <div>
            <strong>Toggle ON (−1h)</strong> during US Daylight Saving Time (2nd Sun Mar → 1st Sun Nov). Keep the +1h
            bias on the CrossChex device itself so the clock face and app display correctly.
          </div>
          <div>
            <strong>Toggle OFF (0h)</strong> when DST ends in November. Remove the bias on the device at the same time.
          </div>
        </div>
      </div>

      {/* Unmapped users */}
      <div className="bg-white rounded border p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Unmapped CrossChex Users <span className="text-sm font-normal text-gray-500">({unmapped.length})</span>
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          These workers have punched on the clock but are not yet linked to an employee record. Their punches are
          ignored until mapped.
        </p>
        {unmapped.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No unmapped users.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2">Work No.</th>
                <th className="text-left p-2">Name (from clock)</th>
                <th className="text-left p-2">Punches</th>
                <th className="text-left p-2">Last Seen</th>
                <th className="text-left p-2">Map to Employee</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map((u) => (
                <tr key={u.crosschex_user_id} className="border-b hover:bg-gray-50">
                  <td className="p-2 font-mono">{u.workno}</td>
                  <td className="p-2">{u.name || '—'}</td>
                  <td className="p-2">{u.punch_count}</td>
                  <td className="p-2 text-gray-600">{fmtDate(u.last_seen_at)}</td>
                  <td className="p-2">
                    <select
                      className="border rounded px-2 py-1 text-sm w-full"
                      value={mapSelections[u.workno] || ''}
                      onChange={(e) =>
                        setMapSelections({ ...mapSelections, [u.workno]: e.target.value })
                      }
                    >
                      <option value="">— Select employee —</option>
                      {employees.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.first_name} {e.last_name} ({e.email})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <button
                      disabled={!mapSelections[u.workno]}
                      onClick={() => handleMap(u.workno)}
                      className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                    >
                      Map
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mapped employees */}
      <div className="bg-white rounded border p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Mapped Employees <span className="text-sm font-normal text-gray-500">({mapped.length})</span>
        </h2>
        {mapped.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No employees mapped yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2">Employee</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Job Title</th>
                <th className="text-left p-2">CrossChex Work No.</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {mapped.map((e) => (
                <tr key={e.id} className="border-b hover:bg-gray-50">
                  <td className="p-2 font-medium">
                    {e.first_name} {e.last_name}
                  </td>
                  <td className="p-2 text-gray-600">{e.email}</td>
                  <td className="p-2 text-gray-600">{e.job_title || '—'}</td>
                  <td className="p-2 font-mono">{e.crosschex_workno}</td>
                  <td className="p-2 text-right">
                    <button
                      onClick={() => handleUnmap(e.id)}
                      className="px-2 py-1 text-xs text-red-700 hover:bg-red-50 border border-red-200 rounded"
                    >
                      Unmap
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
